/**
 * Infrastructure default values and object creation utilities.
 *
 * Provides factory functions for creating new infrastructure entities
 * with consistent default values for protocol statuses and metadata.
 */

import { MANUAL_TYPE } from '../services/DeviceIpSelectorService';
import { INTERFACE_DELETED_FALSE_SOURCE } from '../InfrastructureConstants';

/**
 * Creates protocol status objects for new infrastructure entities.
 *
 * All infrastructure items (vCenters, devices, etc.) start with 'new' status
 * for their monitoring protocols (SNMP, WMI, WinRM).
 *
 * @returns {Object} Protocol status configuration object
 * @returns {Object} .winrm - WinRM protocol configuration
 * @returns {string} .winrm.status - Status value ('new' for new entities)
 * @returns {Object} .wmi - WMI protocol configuration
 * @returns {string} .wmi.status - Status value ('new' for new entities)
 * @returns {Object} .snmp - SNMP protocol configuration
 * @returns {string} .snmp.status - Status value ('new' for new entities)
 *
 * @example
 * const protocols = createNewProtocolStatuses();
 * // { winrm: { status: 'new' }, wmi: { status: 'new' }, snmp: { status: 'new' } }
 */
export function createNewProtocolStatuses() {
    return {
        winrm: {
            status: 'new',
        },
        wmi: {
            status: 'new',
        },
        snmp: {
            status: 'new',
        },
    };
}

/**
 * Creates a base detail object for new infrastructure entities.
 *
 * Combines protocol statuses with common metadata fields that all
 * manually-created infrastructure items share.
 *
 * @param {Object} overrides - Additional fields to merge into the base object
 * @returns {Object} Complete base detail object with protocols and metadata
 *
 * @example
 * // For a generic device
 * const deviceBase = createBaseDetailObject();
 * // { winrm: {...}, wmi: {...}, snmp: {...}, deleted: false, source: 'manual' }
 *
 * @example
 * // For a vCenter with specific fields
 * const vCenterBase = createBaseDetailObject({
 *   ipAddress: '127.0.0.1',
 *   deviceType: 'vcenter',
 *   subType: 'vcenter'
 * });
 */
export function createBaseDetailObject(overrides = {}) {
    return {
        ...createNewProtocolStatuses(),
        ...overrides,
        ...INTERFACE_DELETED_FALSE_SOURCE,
        source: MANUAL_TYPE,
    };
}
